import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { API_PATH } from 'config';
import { getAccessTokenApi } from 'api/auth';
import { useApi } from '.';

/**
 *
 * @param {Number} budgetId - Se ingresa el id del presupuesto del que se necesitan los items
 * @returns - Hook con los items y el refresh
 */
export const useBudgetItemsApi = (budgetId) => {
  const url = `get-items-budget/${budgetId}`;
  const key = 'items';
  const defaultValue = [];
  const [items, refresh] = useApi({
    url,
    key,
    defaultValue,
    successEnabled: false,
  });
  return [items, refresh];
};

/**
 * @deprecated Usar `useBudgetList` — trae toda la lista sin paginar.
 * Se mantiene por si hay consumidores legacy no migrados aún.
 */
export const useBudgetApi = (successEnabled = false) => {
  const url = 'get-budget';
  const key = 'rows';
  const defaultValue = [];
  const [budgets, refresh] = useApi({ url, key, successEnabled, defaultValue });
  return [budgets, refresh];
};

/**
 * Lista paginada de presupuestos con filtros server-side.
 *
 * @param {object} params
 * @param {number} [params.page=1]         1-indexed.
 * @param {number} [params.pageSize=25]
 * @param {string[]|string} [params.stage]  Etapa(s) del ciclo de vida. Array o CSV.
 * @param {string} [params.search]         Match sobre name/reason.
 * @param {number} [params.idClient]
 * @param {string} [params.commune]
 * @returns {{ rows, total, loading, error, refetch }}
 */
export const useBudgetList = ({
  page = 1,
  pageSize = 25,
  stage,
  search,
  idClient,
  commune,
} = {}) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetchList = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const token = getAccessTokenApi();
      const params = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
      };
      if (stage && stage.length) {
        params.stage = Array.isArray(stage) ? stage.join(',') : stage;
      }
      if (search) params.search = search;
      if (idClient) params.idClient = idClient;
      if (commune) params.commune = commune;

      const { data } = await axios.get(`${API_PATH}/get-budget`, {
        params,
        headers: { Authorization: token },
        signal: controller.signal,
      });
      setRows(data.rows ?? []);
      setTotal(data.total ?? 0);
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      setError(err);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, stage, search, idClient, commune]);

  useEffect(() => {
    fetchList();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchList]);

  return { rows, total, loading, error, refetch: fetchList };
};
